import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPortalRmas, createPortalRma, bulkCreatePortalRmas } from "@/lib/portal.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Plus, Loader2, Upload } from "lucide-react";
import { CsvImportDialog } from "@/components/csv-import-dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/rmas")({ component: PortalRmas });

function PortalRmas() {
  const qc = useQueryClient();
  const fetchRmas = useServerFn(listPortalRmas);
  const submitRma = useServerFn(createPortalRma);
  const bulkFn = useServerFn(bulkCreatePortalRmas);
  const { data: payload, isLoading } = useQuery({
    queryKey: ["portal-rmas"],
    queryFn: () => runServerFn(() => fetchRmas(), "Loading RMAs"),
  });
  const rmas = payload?.rmas;
  const customer = payload?.customer;
  const facilities = payload?.facilities ?? [];

  const [open, setOpen] = useState(false);
  const [rmaNumber, setRmaNumber] = useState(""); const [reason, setReason] = useState("");
  const [facilityId, setFacilityId] = useState(""); const [submitting, setSubmitting] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer || !facilityId) return toast.error("Pick a facility");
    setSubmitting(true);
    try {
      await runServerFn(
        () => submitRma({
          data: {
            tenantId: customer.tenant_id,
            customerId: customer.id,
            facilityId,
            rmaNumber: rmaNumber.trim(),
            reason: reason.trim() || null,
          },
        }),
        "Submitting RMA",
      );
      toast.success("RMA submitted");
      setOpen(false); setRmaNumber(""); setReason(""); setFacilityId("");
      qc.invalidateQueries({ queryKey: ["portal-rmas"] });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PortalPageHeader title="Returns" description="Request and track RMAs." actions={
        <div className="flex gap-2 flex-wrap">
        <CsvImportDialog
          trigger={<Button size="sm" variant="outline" disabled={!customer || facilities.length === 0}><Upload className="h-4 w-4 mr-1" />Import RMAs</Button>}
          title="Import RMAs"
          templateHeaders={["rma_number", "reason"]}
          sampleRow={["RMA-2001", "Customer return"]}
          validateRow={(r) => {
            if (!r.rma_number) return { ok: false, error: "rma_number required" };
            return { ok: true, value: r };
          }}
          onImport={async (rows) => {
            if (!customer || facilities.length === 0) {
              return { inserted: 0, failed: rows.map((_, i) => ({ row: i + 2, error: "no customer/facility" })) };
            }
            const res = await runServerFn(() => bulkFn({
              data: {
                tenantId: customer.tenant_id,
                customerId: customer.id,
                facilityId: facilities[0].id,
                rmas: rows.map((r) => ({ rmaNumber: r.rma_number, reason: r.reason || null })),
              },
            }), "Importing RMAs");
            return res;
          }}
          onDone={() => qc.invalidateQueries({ queryKey: ["portal-rmas"] })}
        />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" disabled={!customer}><Plus className="h-4 w-4 mr-2" />New RMA</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Request return</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2"><Label>RMA number</Label><Input required value={rmaNumber} onChange={(e) => setRmaNumber(e.target.value)} placeholder="RMA-1001" /></div>
              <div className="space-y-2"><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Customer return" /></div>
              <div className="space-y-2"><Label>Facility</Label>
                <Select value={facilityId} onValueChange={setFacilityId}>
                  <SelectTrigger><SelectValue placeholder="Pick destination facility" /></SelectTrigger>
                  <SelectContent>{facilities?.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Submit</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      } />
      <div className="page-container">
      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>RMA #</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? (<TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>)
            : rmas && rmas.length > 0 ? rmas.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">
                  <Link to="/portal/rmas/$rmaId" params={{ rmaId: r.id }} className="text-primary hover:underline">
                    {r.rma_number}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">{r.reason ?? "—"}</TableCell>
                <TableCell><StatusPill tone={statusTone(r.status)}>{r.status}</StatusPill></TableCell>
                <TableCell className="text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            )) : (<TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No RMAs yet.</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </div>
      </div>
    </>
  );
}
